import { useState, useMemo, useCallback } from 'react';
import { Scorecard, ScorecardRow, Text } from '@socrait/react';
import { AttendanceData } from '~/services/types';
import { ReviewScorecardsMeeting } from '~/services/review/useReviewScorecardsQuery';
import { MeetingContentState } from '~/pages/review/utils/meetingContentState';

import { getScorecardText, styles as ReviewScorecardStyles } from '../ReviewScoreCards';

type AttendanceScorecardProps = {
  attendanceData?: AttendanceData[];
  contentState: MeetingContentState<ReviewScorecardsMeeting>;
};

const getAttendanceTotal = (data?: AttendanceData[]) => {
  if (!data) return undefined;

  return data.reduce((acc, curr) => {
    return acc + curr.data.length;
  }, 0);
};

export const AttendanceScorecard = ({ attendanceData, contentState }: AttendanceScorecardProps) => {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const total = useMemo(() => getAttendanceTotal(attendanceData), [attendanceData]);
  const showRows = contentState.kind === 'content' && !!total;

  const toggleRow = useCallback(
    (rowId: string) => {
      if (expandedRow === rowId) {
        setExpandedRow(null);
      } else {
        setExpandedRow(rowId);
      }
    },
    [expandedRow],
  );

  return (
    <Scorecard title="Detected Attendance" variant="subtle">
      {showRows ? (
        attendanceData?.map((datum) => {
          const text = datum.title;
          const key = datum.attendanceType;
          const count = datum.data.length;
          const rowData = datum.data.map(({ student }) => {
            return {
              id: student.id,
              name: student.voiceName,
            };
          });

          return (
            <ScorecardRow
              key={`attendance-${key}`}
              rowId={key}
              text={text}
              count={count}
              data={rowData}
              isExpanded={expandedRow === key}
              toggleRow={toggleRow}
            />
          );
        })
      ) : (
        <Text style={ReviewScorecardStyles.noDataText}>
          {getScorecardText('attendance', contentState, attendanceData, total)}
        </Text>
      )}
    </Scorecard>
  );
};
